import React, { useState, useEffect } from 'react';
import LZString from "lz-string";
import './Fulfillment.css';
import { exportWarehouseViewToPDF } from './fulfillmentExport';
import { jsPDF } from 'jspdf';
import ShippingSync from './ShippingSync';

const API_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const Fulfillment = () => {
    const [allOrders, setAllOrders] = useState([]);
    const [unfulfilledOrders, setUnfulfilledOrders] = useState([]);
    const [warehouseView, setWarehouseView] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('warehouse'); // 'warehouse' or 'orders' or 'shipping-sync'
    const [ordersFilter, setOrdersFilter] = useState('unfulfilled'); // 'unfulfilled' or 'all'
    const [productImages, setProductImages] = useState({});
    const [loadingStatus, setLoadingStatus] = useState('');
    const [selectedOrders, setSelectedOrders] = useState({});

    // Helper function to extract image URL from line item
    const getImageUrl = (item) => {
        // Try multiple possible paths for the image
        return item.image_url ||
               item.image?.src ||
               item.featured_image?.src ||
               item.image ||
               null;
    };

    // Fetch product images via backend API
    const fetchProductImagesGraphQL = async (productIds) => {
        if (!productIds || productIds.length === 0) return {};

        setLoadingStatus(`Fetching ${productIds.length} product images...`);

        try {
            const response = await fetch(`${API_BASE_URL}/api/batch-product-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ product_ids: productIds })
            });

            if (response.ok) {
                const data = await response.json();
                setLoadingStatus('');
                console.log(`Fetched ${Object.keys(data.images).length} images from backend`);
                return data.images || {};
            } else {
                console.error('Backend error:', response.status, response.statusText);
                setLoadingStatus('');
                return {};
            }
        } catch (error) {
            console.error('Error fetching images from backend:', error);
            setLoadingStatus('');
            return {};
        }
    };

    useEffect(() => {
        const processOrders = async () => {
            setLoadingStatus('Processing orders...');
            const compressedData = sessionStorage.getItem("rawOrdersData");
            if (!compressedData) {
                setError("No orders data found. Please fetch orders first.");
                setIsLoading(false);
                return;
            }

            try {
                const orders = JSON.parse(LZString.decompress(compressedData));

                // Extract unique product IDs from all orders to fetch images
                const allProductIds = new Set();
                orders.forEach(order => {
                    order.line_items.forEach(item => {
                        if (item.product_id) {
                            allProductIds.add(item.product_id);
                        }
                    });
                });

                console.log(`Found ${allProductIds.size} unique products to fetch images for`);

                // Store all orders first
                setAllOrders(orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
                
                // Filter for unfulfilled orders
                const unfulfilled = orders.filter(order => 
                    order.cancelled_at === null && 
                    !order.void && 
                    !order.archived &&
                    order.financial_status !== 'refunded' &&
                    (order.fulfillment_status === null || order.fulfillment_status === "unfulfilled")
                ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                setUnfulfilledOrders(unfulfilled);

                // Fetch images from Shopify GraphQL API
                const images = await fetchProductImagesGraphQL(Array.from(allProductIds));
                console.log('Fetched images:', images);

                // Process warehouse view
                const warehouseItems = {};

                unfulfilled.forEach(order => {
                    order.line_items.forEach(item => {
                        const key = `${item.variant_id}-${item.title}-${item.variant_title}`;
                        if (!warehouseItems[key]) {
                            warehouseItems[key] = {
                                title: item.title,
                                variant_title: item.variant_title,
                                sku: item.sku,
                                product_id: item.product_id,
                                image_url: item.product_id && images[item.product_id] ? images[item.product_id] : null,
                                total_quantity: 0,
                                orders: []
                            };
                        }
                        warehouseItems[key].total_quantity += item.quantity;
                        warehouseItems[key].orders.push({
                            order_number: order.order_number,
                            quantity: item.quantity,
                            customer: `${order.shipping_address?.first_name} ${order.shipping_address?.last_name}`,
                            created_at: order.created_at
                        });
                    });
                });

                // Update ALL orders with images from Shopify GraphQL
                const updatedAllOrders = orders.map(order => ({
                    ...order,
                    line_items: order.line_items.map(item => ({
                        ...item,
                        image_url: item.product_id && images[item.product_id] ? images[item.product_id] : null
                    }))
                })).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                // Update unfulfilled orders with images from Shopify GraphQL
                const updatedUnfulfilledOrders = unfulfilled.map(order => ({
                    ...order,
                    line_items: order.line_items.map(item => ({
                        ...item,
                        image_url: item.product_id && images[item.product_id] ? images[item.product_id] : null
                    }))
                }));

                const sortedProducts = Array.from(Object.values(warehouseItems).sort((a, b) => {
                    const skuA = a.sku || '';
                    const skuB = b.sku || '';
                    return skuA.localeCompare(skuB);
                }));

                setAllOrders(updatedAllOrders);
                setUnfulfilledOrders(updatedUnfulfilledOrders);
                setWarehouseView(sortedProducts);
                setIsLoading(false);
                setLoadingStatus('');

            } catch (error) {
                console.error("Error processing orders:", error);
                setError("Error processing orders data");
                setIsLoading(false);
                setLoadingStatus('');
            }
        };

        processOrders();
    }, []);

    const handleExport = () => {
        const totalItems = Object.values(warehouseView)
            .reduce((sum, item) => sum + item.total_quantity, 0);
        exportWarehouseViewToPDF(warehouseView, totalItems);
    };

    const resizeImage = (imageUrl) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            
            img.onload = () => {
                // Create a canvas with slightly larger dimensions
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                // Increased size for better quality
                canvas.width = 100;
                canvas.height = 100;
                
                // Draw and resize image
                ctx.drawImage(img, 0, 0, 100, 100);
                
                // Get as base64 with better quality
                resolve(canvas.toDataURL('image/jpeg', 0.7));
            };
            
            img.onerror = () => {
                console.error('Error loading image for resize');
                resolve(null);
            };
            
            img.src = imageUrl;
        });
    };

    const handleExportThermal = async () => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [102, 152] // 4x6 inches in mm
        });

        // Define margins
        const margin = {
            left: 12,
            right: 12,
            top: 12,
            bottom: 12
        };
        const contentWidth = 102 - margin.left - margin.right;

        // Helper function to wrap text
        const wrapText = (text, fontSize, maxWidth) => {
            doc.setFontSize(fontSize);
            const words = text.split(' ');
            const lines = [];
            let currentLine = words[0];

            for (let i = 1; i < words.length; i++) {
                const word = words[i];
                const width = doc.getStringUnitWidth(currentLine + ' ' + word) * fontSize / doc.internal.scaleFactor;
                if (width < maxWidth) {
                    currentLine += ' ' + word;
                } else {
                    lines.push(currentLine);
                    currentLine = word;
                }
            }
            lines.push(currentLine);
            return lines;
        };

        // Get the orders to export
        let ordersToExport = [];
        if (ordersFilter === 'unfulfilled') {
            ordersToExport = unfulfilledOrders;
        } else {
            // Filter orders based on selection
            ordersToExport = allOrders.filter(order => selectedOrders[order.id]);
        }

        // Reverse the orders array so the first is last and the last is first
        ordersToExport = [...ordersToExport].reverse();

        // Debug: Log first order structure to see available fields
        if (ordersToExport.length > 0) {
            console.log('Sample order structure:', {
                shipping_address: ordersToExport[0].shipping_address,
                billing_address: ordersToExport[0].billing_address,
                customer: ordersToExport[0].customer,
                email: ordersToExport[0].email
            });
        }

        for (let i = 0; i < ordersToExport.length; i++) {
            const order = ordersToExport[i];
            if (i > 0) {
                doc.addPage();
            }

            let currentY = margin.top;

            // Header section - Order number and date on same line
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text(`#${order.order_number}`, margin.left, currentY);
            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            doc.text(new Date(order.created_at).toLocaleDateString(), 102 - margin.right - 20, currentY);
            currentY += 5;

            // Customer info section
            doc.setFontSize(8);

            // Get customer name from shipping, billing, or customer object
            const shippingAddr = order.shipping_address || {};
            const billingAddr = order.billing_address || {};
            const customer = order.customer || {};

            const firstName = shippingAddr.first_name || billingAddr.first_name || customer.first_name || '';
            const lastName = shippingAddr.last_name || billingAddr.last_name || customer.last_name || '';
            const customerName = `${firstName} ${lastName}`.trim() || 'N/A';

            doc.text(customerName, margin.left, currentY);
            currentY += 3.5;

            const email = order.email || customer.email || '';
            if (email) {
                doc.text(email, margin.left, currentY);
                currentY += 3.5;
            }

            // Build address from available fields
            const addressParts = [
                shippingAddr.address1 || billingAddr.address1,
                shippingAddr.city || billingAddr.city,
                shippingAddr.zip || billingAddr.zip
            ].filter(Boolean); // Remove empty values

            if (addressParts.length > 0) {
                const address = addressParts.join(', ');
                const wrappedAddress = wrapText(address, 8, contentWidth);
                wrappedAddress.forEach((line, index) => {
                    doc.text(line, margin.left, currentY);
                    currentY += 3.5;
                });
            }
            currentY += 2;

            // Process all images first to avoid async issues
            const processedImages = await Promise.all(
                order.line_items.map(async item => {
                    if (item.image_url) {
                        try {
                            return await resizeImage(item.image_url);
                        } catch (error) {
                            console.error('Error processing image:', error);
                            return null;
                        }
                    }
                    return null;
                })
            );

            // Items section
            for (let j = 0; j < order.line_items.length; j++) {
                const item = order.line_items[j];
                const resizedImage = processedImages[j];

                // Draw delimiter line with minimal spacing
                if (j > 0) {
                    doc.setDrawColor(200, 200, 200);
                    doc.line(margin.left, currentY - 1, 102 - margin.right, currentY - 1);
                    currentY += 1;
                }

                const lineStartY = currentY;

                // Add resized item image
                if (resizedImage) {
                    try {
                        doc.addImage(resizedImage, 'JPEG', margin.left, currentY, 10, 10);
                    } catch (error) {
                        console.error('Error adding image to PDF:', error);
                    }
                }

                // Quantity in square box
                const boxSize = 6;
                doc.setDrawColor(0);
                doc.setFillColor(255, 255, 255);
                doc.rect(margin.left + 13, currentY + 2, boxSize, boxSize, 'FD');
                doc.setFont(undefined, 'bold');
                doc.setFontSize(8);
                const qtyText = item.quantity.toString();
                const textWidth = doc.getStringUnitWidth(qtyText) * 8 / doc.internal.scaleFactor;
                const textX = margin.left + 13 + (boxSize - textWidth) / 2;
                const textY = currentY + 6;
                doc.text(qtyText, textX, textY);

                // Item details with title and variant on separate lines
                const startX = margin.left + 22;
                doc.setFont(undefined, 'bold');
                doc.setFontSize(8);
                const wrappedTitle = wrapText(item.title, 8, contentWidth - 22);
                wrappedTitle.forEach((line, index) => {
                    doc.text(line, startX, currentY + 3 + (index * 3));
                });

                // Variant title below title with minimal spacing
                const titleHeight = wrappedTitle.length * 3;
                doc.setFont(undefined, 'normal');
                doc.setFontSize(8);
                const variantText = item.variant_title || 'Default';
                doc.text(variantText, startX, currentY + 1.5 + titleHeight + 2.5);
                
                // SKU below variant with minimal spacing
                const skuText = `SKU: ${item.sku || 'N/A'}`;
                doc.text(skuText, startX, currentY + 2.5 + titleHeight + 5);

                // Calculate height needed for this item - more compact
                const itemHeight = Math.max(
                    10, // Image height
                    3 + titleHeight + 7 // Text height (title + variant + SKU with minimal spacing)
                );
                currentY += itemHeight;
            }

            // Footer section
            currentY += 2;
            doc.setDrawColor(150, 150, 150);
            doc.line(margin.left, currentY, 102 - margin.right, currentY);
            currentY += 3;
            
            // Shipping info
            if (order.shipping_lines && order.shipping_lines.length > 0) {
                doc.setFont(undefined, 'normal');
                doc.setFontSize(8);
                doc.text(`Shipping: ${order.shipping_lines[0].title}`, margin.left, currentY);
                currentY += 4;
            }

            // Total amount
            doc.setFont(undefined, 'bold');
            doc.setFontSize(9);
            const totalText = `Total: ${order.total_price} ${order.currency}`;
            doc.text(totalText, margin.left, currentY);
        }

        doc.save('thermal_orders.pdf');
    };

    const toggleOrderSelection = (orderId) => {
        setSelectedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    const toggleAllOrders = () => {
        if (Object.keys(selectedOrders).length === allOrders.length) {
            // If all are selected, deselect all
            setSelectedOrders({});
        } else {
            // Otherwise, select all
            const newSelection = {};
            allOrders.forEach(order => {
                newSelection[order.id] = true;
            });
            setSelectedOrders(newSelection);
        }
    };

    const getSelectedOrdersCount = () => {
        return Object.values(selectedOrders).filter(Boolean).length;
    };

    if (isLoading) {
        return (
            <div className="fulfillment-loading">
                <div className="loading-spinner"></div>
                <div className="loading-text">
                    {loadingStatus || 'Loading orders...'}
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="fulfillment-error">{error}</div>;
    }

    const renderWarehouseView = () => (
        <div className="warehouse-view">
            <div className="warehouse-header">
                <div className="header-left">
                    <h3>Warehouse Picking List</h3>
                    <div className="total-items">
                        Total to Pick: {Object.values(warehouseView).reduce((sum, item) => sum + item.total_quantity, 0)}
                    </div>
                </div>
                <button className="export-button" onClick={handleExport}>
                    Export PDF
                </button>
            </div>
            <div className="warehouse-items">
                {Object.entries(warehouseView).map(([key, item]) => (
                    <div key={key} className="warehouse-item">
                        <div className="item-image-container">
                            {item.image_url ? (
                                <img src={item.image_url} alt={item.title} className="item-image" />
                            ) : (
                                <div className="no-image">No Image</div>
                            )}
                        </div>
                        <div className="item-info">
                            <div className="item-header">
                                <div className="title-section">
                                    <h4>{item.title}</h4>
                                    <span className="sku-code">{item.sku || 'No SKU'}</span>
                                </div>
                                <div className="total-quantity">
                                    {item.total_quantity}
                                </div>
                            </div>
                            <div className="variant-info">
                                <span className="variant-size">{item.variant_title || 'Default'}</span>
                            </div>
                        </div>
                        <div className="order-distribution">
                            <div className="distribution-list">
                                {item.orders.map((order, index) => (
                                    <div key={index} className="distribution-item">
                                        <span className="order-number">#{order.order_number}</span>
                                        <span className="quantity">{order.quantity}</span>
                                        <span className="customer">{order.customer}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderOrdersView = () => {
        const ordersToShow = ordersFilter === 'unfulfilled' ? unfulfilledOrders : allOrders;
        
        return (
            <>
                <div className="orders-header">
                    <div className="header-left">
                        <h2>Orders</h2>
                        <div className="order-filters">
                            <button 
                                className={`filter-button ${ordersFilter === 'unfulfilled' ? 'active' : ''}`}
                                onClick={() => setOrdersFilter('unfulfilled')}
                            >
                                Unfulfilled Orders
                            </button>
                            <button 
                                className={`filter-button ${ordersFilter === 'all' ? 'active' : ''}`}
                                onClick={() => setOrdersFilter('all')}
                            >
                                All Orders
                            </button>
                        </div>
                    </div>
                    <div className="orders-actions">
                        {ordersFilter === 'all' && (
                            <>
                                <div className="selection-info">
                                    <button className="select-all-button" onClick={toggleAllOrders}>
                                        {Object.keys(selectedOrders).length === allOrders.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                    <span className="selected-count">
                                        {getSelectedOrdersCount()} orders selected
                                    </span>
                                </div>
                            </>
                        )}
                        <button 
                            className="export-button" 
                            onClick={handleExportThermal}
                            disabled={ordersFilter === 'all' && getSelectedOrdersCount() === 0}
                        >
                            Export Thermal
                        </button>
                    </div>
                </div>
                <div className="orders-count">
                    Total orders: {ordersToShow.length}
                </div>
                <div className="orders-grid">
                    {ordersToShow.map(order => (
                        <div key={order.id} className="order-card">
                            {ordersFilter === 'all' && (
                                <div className="order-selection">
                                    <input 
                                        type="checkbox" 
                                        checked={!!selectedOrders[order.id]} 
                                        onChange={() => toggleOrderSelection(order.id)}
                                    />
                                </div>
                            )}
                            <div className="order-header">
                                <h3>Order #{order.order_number}</h3>
                                <span className="order-date">
                                    {new Date(order.created_at).toLocaleDateString()}
                                </span>
                            </div>
                            <div className="customer-info">
                                <p>
                                    {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                                </p>
                                <p>{order.email}</p>
                            </div>
                            <div className="order-items">
                                <h4>Items:</h4>
                                {order.line_items.map(item => (
                                    <div key={item.id} className="order-item">
                                        <div className="item-image-container">
                                            {item.image_url ? (
                                                <img src={item.image_url} alt={item.title} className="item-image" />
                                            ) : (
                                                <div className="no-image">No Image</div>
                                            )}
                                        </div>
                                        <div className="item-details">
                                            <div className="item-quantity">{item.quantity}x</div>
                                            <div className="item-title">{item.title}</div>
                                            <div className="item-variant">
                                                {item.variant_title || 'Default'}
                                            </div>
                                            <div className="item-sku">
                                                SKU: {item.sku || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="order-total">
                                Total: {order.total_price} {order.currency}
                            </div>
                            <div className="order-status">
                                Status: {order.fulfillment_status || 'Unfulfilled'}
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    return (
        <div className="fulfillment-container">
            <div className="view-controls">
                <button 
                    className={`view-button ${viewMode === 'warehouse' ? 'active' : ''}`}
                    onClick={() => setViewMode('warehouse')}
                >
                    Warehouse View
                </button>
                <button 
                    className={`view-button ${viewMode === 'orders' ? 'active' : ''}`}
                    onClick={() => setViewMode('orders')}
                >
                    Orders View
                </button>
                <button 
                    className={`view-button ${viewMode === 'shipping-sync' ? 'active' : ''}`}
                    onClick={() => setViewMode('shipping-sync')}
                >
                    Shipping Sync
                </button>
            </div>

            {viewMode === 'warehouse' ? renderWarehouseView() : 
             viewMode === 'orders' ? renderOrdersView() : 
             <ShippingSync />}
        </div>
    );
};

export default Fulfillment; 